import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import type { WishlistVisibility } from '@nexus/customer/wishlists/model';
import {
  UiBottomSheetComponent,
  UiButtonComponent,
  UiChoiceGroupComponent,
  UiInlineMessageComponent,
  UiInputComponent,
} from '@nexus/frontend-shared-ui';
import type { WishlistAccessSharingDrawerView } from '../../types/wishlist-access-sharing-drawer-view.interface';
import type { WishlistAccessSharingVisibilityOption } from '../../types/wishlist-access-sharing-visibility-option.interface';
import { WishlistAccessPanelComponent } from '../wishlist-access-panel/wishlist-access-panel.component';
import { WishlistStatusPillComponent } from '../wishlist-status-pill/wishlist-status-pill.component';
import {
  WISHLIST_ACCESS_SHARING_EMAIL_PATTERN,
  WISHLIST_ACCESS_SHARING_VISIBILITY_OPTIONS,
} from './wishlist-access-sharing-drawer.constants';

@Component({
  selector: 'wishlist-access-sharing-drawer',
  imports: [
    UiBottomSheetComponent,
    UiButtonComponent,
    UiChoiceGroupComponent,
    UiInlineMessageComponent,
    UiInputComponent,
    WishlistAccessPanelComponent,
    WishlistStatusPillComponent,
  ],
  templateUrl: './wishlist-access-sharing-drawer.component.html',
  styleUrl: './wishlist-access-sharing-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistAccessSharingDrawerComponent {
  public readonly open: InputSignal<boolean> = input<boolean>(false);
  public readonly view: InputSignal<WishlistAccessSharingDrawerView | null> =
    input<WishlistAccessSharingDrawerView | null>(null);
  public readonly loading: InputSignal<boolean> = input<boolean>(false);
  public readonly fallbackError: InputSignal<string | null> = input<string | null>(null);
  public readonly closed: OutputEmitterRef<void> = output<void>();
  public readonly visibilityChanged: OutputEmitterRef<WishlistVisibility> = output<WishlistVisibility>();
  public readonly createLinkSelected: OutputEmitterRef<void> = output<void>();
  public readonly copyLinkSelected: OutputEmitterRef<void> = output<void>();
  public readonly rotateLinkSelected: OutputEmitterRef<void> = output<void>();
  public readonly disableLinkSelected: OutputEmitterRef<void> = output<void>();
  public readonly inviteSubmitted: OutputEmitterRef<string> = output<string>();
  public readonly invitationRemoved: OutputEmitterRef<string> = output<string>();

  protected readonly options: Signal<readonly WishlistAccessSharingVisibilityOption[]> = computed(
    (): readonly WishlistAccessSharingVisibilityOption[] => {
      const pending: boolean = this.view()?.pending ?? false;

      return WISHLIST_ACCESS_SHARING_VISIBILITY_OPTIONS.map(
        (option: WishlistAccessSharingVisibilityOption): WishlistAccessSharingVisibilityOption => ({
          ...option,
          disabled: pending,
        }),
      );
    },
  );
  protected readonly invitedEmail: WritableSignal<string> = signal<string>('');
  protected readonly inviteTouched: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly shouldShowInviteComposer: Signal<boolean> = computed((): boolean => {
    return this.view()?.visibility === 'invited_only';
  });
  protected readonly shouldShowInvitations: Signal<boolean> = computed((): boolean => {
    const view: WishlistAccessSharingDrawerView | null = this.view();

    return !!view && (view.visibility === 'invited_only' || view.invitations.length > 0);
  });
  protected readonly invitedEmailValidationError: Signal<string | null> = computed((): string | null =>
    this.resolveInvitedEmailError(),
  );
  protected readonly invitedEmailError: Signal<string | null> = computed((): string | null => {
    return this.inviteTouched() ? this.invitedEmailValidationError() : null;
  });
  protected readonly canSubmitInvite: Signal<boolean> = computed((): boolean => {
    const view: WishlistAccessSharingDrawerView | null = this.view();

    return (
      !!view &&
      view.visibility === 'invited_only' &&
      !view.pending &&
      this.invitedEmail().trim().length > 0
    );
  });
  private readonly resetClosedDraft = effect((): void => {
    if (!this.open()) {
      this.resetDraft();
    }
  });

  protected handleClosed(): void {
    this.resetDraft();
    this.closed.emit();
  }

  protected handleVisibilitySelected(visibility: WishlistVisibility): void {
    const view: WishlistAccessSharingDrawerView | null = this.view();

    if (!view || view.pending || view.visibility === visibility) {
      return;
    }

    this.visibilityChanged.emit(visibility);
  }

  protected handleVisibilityOptionSelected(optionId: string): void {
    if (!isWishlistVisibility(optionId)) {
      return;
    }

    this.handleVisibilitySelected(optionId);
  }

  protected handleCreateLinkSelected(): void {
    if (this.view()?.pending) {
      return;
    }

    this.createLinkSelected.emit();
  }

  protected handleCopyLinkSelected(): void {
    if (!this.view()?.canCopyLink || this.view()?.pending) {
      return;
    }

    this.copyLinkSelected.emit();
  }

  protected handleRotateLinkSelected(): void {
    if (!this.view()?.canRotateLink || this.view()?.pending) {
      return;
    }

    this.rotateLinkSelected.emit();
  }

  protected handleDisableLinkSelected(): void {
    if (!this.view()?.canDisableLink || this.view()?.pending) {
      return;
    }

    this.disableLinkSelected.emit();
  }

  protected handleInviteEmailChanged(value: string): void {
    this.invitedEmail.set(value);
  }

  protected handleInviteSubmitted(): void {
    this.inviteTouched.set(true);

    if (!this.canSubmitInvite() || this.invitedEmailValidationError() !== null) {
      return;
    }

    const email: string = this.invitedEmail().trim();
    this.resetDraft();
    this.inviteSubmitted.emit(email);
  }

  protected handleInvitationRemoved(invitationId: string): void {
    if (this.view()?.pending) {
      return;
    }

    this.invitationRemoved.emit(invitationId);
  }

  private resolveInvitedEmailError(): string | null {
    const email: string = this.invitedEmail().trim();

    if (!email) {
      return 'Podaj email zaproszonej osoby.';
    }

    if (email.length > 320) {
      return 'Email jest za długi.';
    }

    if (!WISHLIST_ACCESS_SHARING_EMAIL_PATTERN.test(email)) {
      return 'Podaj poprawny email.';
    }

    return null;
  }

  private resetDraft(): void {
    this.invitedEmail.set('');
    this.inviteTouched.set(false);
  }
}

function isWishlistVisibility(value: string): value is WishlistVisibility {
  return value === 'private' || value === 'household' || value === 'invited_only' || value === 'anyone_with_link';
}
